import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "gp_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
    baseURL: API_BASE,
    timeout: 30000,
});

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        if (error?.response?.status === 401) {
            clearToken();
            if (typeof window !== "undefined" && window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        }
        return Promise.reject(error);
    },
);

export const wsUrl = () => {
    const base = BACKEND_URL.replace(/^http/, "ws");
    return `${base}/api/ws/events`;
};

// Auth
export const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    setToken(data.access_token);
    return data;
};
export const me = async () => (await api.get("/auth/me")).data;

// Overview
export const overview = async () => (await api.get("/stats/overview")).data;
export const timeseries = async (days = 7) => (await api.get(`/stats/timeseries?days=${days}`)).data;

// Groups / Channels
export const listGroups = async () => (await api.get("/groups")).data;
export const getGroup = async (chatId) => (await api.get(`/groups/${chatId}`)).data;
export const updateGroupSettings = async (chatId, settings) => (await api.put(`/groups/${chatId}/settings`, settings)).data;
export const groupEvents = async (chatId, limit = 50) => (await api.get(`/groups/${chatId}/events?limit=${limit}`)).data;
export const groupStats = async (chatId) => (await api.get(`/groups/${chatId}/stats`)).data;

// Events
export const listEvents = async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return (await api.get(`/events${q ? `?${q}` : ""}`)).data;
};

// Alerts
export const listAlerts = async (onlyUnacked = false) =>
    (await api.get(`/alerts${onlyUnacked ? "?only_unacked=true" : ""}`)).data;
export const ackAlert = async (id) => (await api.post(`/alerts/${id}/ack`)).data;

// AI
export const listAi = async (onlyScam = false) =>
    (await api.get(`/ai/analyses${onlyScam ? "?only_scam=true" : ""}`)).data;
export const reviewAi = async (id, review) =>
    (await api.put(`/ai/analyses/${id}/review?review=${review}`)).data;

// Lists
export const listEntries = async (kind) => (await api.get(`/lists/${kind}`)).data;
export const addEntry = async (kind, payload) => (await api.post(`/lists/${kind}`, payload)).data;
export const removeEntry = async (kind, userId, chatId = 0) =>
    (await api.delete(`/lists/${kind}/${userId}?chat_id=${chatId}`)).data;

// Bot
export const botInfo = async () => (await api.get("/bot/info")).data;

// ==========================================
// V2 APIs
// ==========================================

// Channel analytics
export const channelsOverview = async () => (await api.get("/stats/channels-overview")).data;
export const channelGrowth = async (chatId, days = 7) =>
    (await api.get(`/stats/channel/${chatId}/growth?days=${days}`)).data;
export const channelJoinsLeaves = async (chatId, days = 7) =>
    (await api.get(`/stats/channel/${chatId}/joins-leaves?days=${days}`)).data;
export const channelInviteSources = async (chatId) =>
    (await api.get(`/stats/channel/${chatId}/invite-sources`)).data;

// Telegram native stats (via MTProto)
export const channelTelegramStats = async (chatId) => {
    try {
        return (await api.get(`/stats/channel/${chatId}/telegram-stats`)).data;
    } catch (e) {
        const status = e?.response?.status;
        if (status === 503) return { error: "MTProto غير متصل" };
        if (status === 404) return { error: "غير متوفرة — تأكد أن MTProto أدمن" };
        return null;
    }
};
export const allTelegramStats = async () => {
    try {
        return (await api.get("/stats/all-telegram-stats")).data;
    } catch {
        return { stats: {} };
    }
};

// Invite Links (V2)
export const channelInviteLinks = async (chatId) =>
    (await api.get(`/channels/${chatId}/invite-links`)).data;
export const inviteLinkHistory = async (chatId, link, hours = 24) =>
    (await api.get(`/channels/${chatId}/invite-links/history?link=${encodeURIComponent(link)}&hours=${hours}`)).data;

// ==========================================
// Premium Intelligence (owner-only)
// ==========================================
export const premiumOverview = async () => (await api.get("/stats/premium-overview")).data;
export const premiumBatches = async () => (await api.get("/stats/premium-batches")).data;
export const activeAudience = async () => (await api.get("/stats/active-audience")).data;
export const premiumUpdates = async () => (await api.get("/stats/premium-updates")).data;
export const premiumChannel = async (chatId, days = 30) =>
    (await api.get(`/stats/premium-channel/${chatId}?days=${days}`)).data;

// Allowlist (V2)
export const getAllowlist = async () => (await api.get("/allowlist")).data;
export const addToAllowlist = async (userId, note = "") =>
    (await api.post("/allowlist", { user_id: userId, note })).data;
export const removeFromAllowlist = async (userId) =>
    (await api.delete(`/allowlist/${userId}`)).data;
